import { UserRoundIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { usePages, useSite } from '../../contexts/SiteContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { teamOnSite } from '../../core/rules/salonDays';
import { fill } from '../../utils/copy';
import { pageActions } from '../../data/pageActions';
import { quietLink, sectionHeading } from '../../ui/sections';

/**
 * "Ook in de salon": de collega's naast Sarah, op de salonpagina, want daar kies je bij wie je
 * komt. Alleen wie Sarah in Instellingen › Team op "Toon op de salonpagina" zet; zonder zulke
 * collega's staat het blok er niet (de hero noemt Sarah al).
 *
 * Per collega: portret, naam, één zin, de alinea en welke behandelingen zij doet. Eén collega:
 * portret en tekst naast elkaar over de volle breedte; meer: twee kolommen.
 */
export function SalonTeam() {
  const pages = usePages();
  const site = useSite();
  const {
    treatments
  } = useCatalog();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.treatments.team;
  const colleagues = teamOnSite(site.staff);
  if (colleagues.length === 0) return null;
  const doesFor = (allTreatments: boolean, slugs: string[]) => allTreatments ? ['Alle behandelingen'] : treatments.filter(t => slugs.includes(t.slug)).map(t => t.name);
  const single = colleagues.length === 1;
  return <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="salon-team" data-id="element-8472" data-mp="2jsajnham8utf">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8473" data-mp="2or3z3f9gqvw8">
        <div className="max-w-2xl" data-id="element-8474" data-mp="2tp5ki6ud37av">
          <h2 id="salon-team" className={`${sectionHeading} text-ink`} data-id="element-8475" data-mp="2to46bti1u4ox">
            <HeadingText heading={copy.heading} data-id="element-8476" data-mp="2yflzvwhqt2zv" />
          </h2>
          {copy.body && <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-8477" data-mp="2yhoteqi3te9c">{fill(copy.body)}</p>}
        </div>

        <ul className={`mt-12 grid gap-12 lg:mt-14 ${single ? '' : 'md:grid-cols-2 md:gap-10'}`} data-id="element-8478" data-mp="25zkxncv37pwb">
          {colleagues.map(s => {
          const does = doesFor(s.allTreatments, s.treatments);
          return <li key={s.id} data-id="element-8479" data-mp="1t0bpyygj1e5y">
                <article aria-label={s.name} className={`grid gap-6 ${single ? 'sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16' : 'grid-cols-[7rem_minmax(0,1fr)] sm:grid-cols-[9rem_minmax(0,1fr)]'}`} data-id="element-8480" data-mp="1sz93d2q8lxvu">
                  {s.photo ? <img src={s.photo} alt={`Portret van ${s.name}`} loading="lazy" className="aspect-[4/5] w-full object-cover" data-id="element-8481" data-mp="1a0xxkkyg4bhk" /> : <span className="flex aspect-[4/5] w-full items-center justify-center bg-shell text-muted" aria-hidden="true" data-id="element-8482" data-mp="1ozrv61jfemmz">
                      <UserRoundIcon className="h-8 w-8" strokeWidth={1.25} data-id="element-8483" data-mp="1k1glprx1me5a" />
                    </span>}
                  <div className={`min-w-0 ${single ? 'sm:self-center' : ''}`} data-id="element-8484" data-mp="2vesixjyaryih">
                    <h3 className={`${single ? 'type-sub' : 'type-title'} text-ink`} data-id="element-8485" data-mp="32xs0dk277fz7">{s.name}</h3>
                    {s.intro && <p className="mt-1.5 text-[15px] text-ink" data-id="element-8486" data-mp="37wlhdmgp1p44">{s.intro}</p>}
                    {s.bio && <p className="mt-4 max-w-xl whitespace-pre-line text-[17px] leading-relaxed text-muted" data-id="element-8487" data-mp="2tx3mqqgpvhmu">{s.bio}</p>}
                    {does.length > 0 && <ul aria-label={`Behandelingen van ${s.name}`} className="mt-5 flex flex-wrap gap-2" data-id="element-8488" data-mp="2xz794a4xc6mw">
                        {does.map(name => <li key={name} className="rounded-full border border-ink/15 px-3 py-1 text-[13px] text-ink" data-id="element-8489" data-mp="3qnw21lvtt5nk">
                            {name}
                          </li>)}
                      </ul>}
                    <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={`mt-5 ${quietLink} text-ink underline decoration-ink/25 hover:decoration-ink`} data-id="element-8490" data-mp="3gvgkgpofyj37">
                      {pageActions.book} bij {s.name}
                    </button>
                  </div>
                </article>
              </li>;
        })}
        </ul>
      </div>
    </section>;
}